import React, { useState } from 'react';
import { Loader2, ArrowRight, Zap, Package, DollarSign, Sparkles } from 'lucide-react';

interface StartTrialCTAProps {
  isAuthenticated: boolean;
  onLoginClick: () => void;
}

const FALLBACK_URL = 'https://myshop.coffee/product/founding-partner/';

const StartTrialCTA: React.FC<StartTrialCTAProps> = ({ isAuthenticated, onLoginClick }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleStartTrial = async () => {
    setIsLoading(true);

    try {
      const response = await fetch('/api/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();

      if (data.success && data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        window.location.href = FALLBACK_URL;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      window.location.href = FALLBACK_URL;
    }
  };

  return (
    <div className="min-h-full bg-neutral-50 px-5 py-8">
      {/* Hero - Exaggerated Minimalism */}
      <div className="mb-10">
        <h1 className="text-4xl font-bold text-neutral-900 tracking-tight leading-tight mb-4">
          Your coffee brand.
          <br />
          <span className="text-neutral-400">Zero hassle.</span>
        </h1>
        <p className="text-lg text-neutral-500 leading-relaxed">
          We handle roasting, packing, and shipping. You build your brand.
        </p>
      </div>

      {/* Primary CTA - Large, Confident */}
      <button
        onClick={handleStartTrial}
        disabled={isLoading}
        className="w-full bg-neutral-900 text-white py-5 px-8 rounded-2xl font-semibold text-lg mb-10 transition-all duration-200 hover:bg-neutral-800 active:scale-[0.98] disabled:opacity-60 flex items-center justify-center"
      >
        {isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <>
            Start Free Trial
            <ArrowRight className="w-5 h-5 ml-2" />
          </>
        )}
      </button>

      {/* Bento Grid - Key Value Props */}
      <div className="grid grid-cols-2 gap-3 mb-10">
        {/* Large tile */}
        <div className="col-span-2 bg-white rounded-3xl p-6 border border-neutral-200/60">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-400 mb-1">Avg. profit per bag</p>
              <p className="text-5xl font-bold text-neutral-900 tracking-tight">$8</p>
            </div>
            <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-emerald-600" />
            </div>
          </div>
        </div>

        {/* Smaller tiles */}
        <div className="bg-white rounded-3xl p-5 border border-neutral-200/60">
          <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center mb-3">
            <Package className="w-5 h-5 text-amber-600" />
          </div>
          <p className="text-sm font-medium text-neutral-400 mb-0.5">Inventory</p>
          <p className="text-xl font-bold text-neutral-900">$0</p>
        </div>

        <div className="bg-white rounded-3xl p-5 border border-neutral-200/60">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center mb-3">
            <Zap className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-sm font-medium text-neutral-400 mb-0.5">Setup time</p>
          <p className="text-xl font-bold text-neutral-900">24h</p>
        </div>

        {/* Wide tile */}
        <div className="col-span-2 bg-gradient-to-br from-neutral-900 to-neutral-800 rounded-3xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-400 mb-1">Your brand on every bag</p>
              <p className="text-lg font-semibold">Custom labels included</p>
            </div>
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
          </div>
        </div>
      </div>

      {/* Social Proof - Subtle */}
      <div className="text-center">
        <p className="text-sm text-neutral-400">
          Trusted by <span className="text-neutral-600 font-medium">500+</span> coffee entrepreneurs
        </p>
      </div>

      {/* Terms - Minimal */}
      <p className="text-center text-xs text-neutral-400 mt-6">
        7-day free trial · Cancel anytime · Card required
      </p>
    </div>
  );
};

export default StartTrialCTA;
